<template>
  <div class="pt-3 pb-5">
    <div class="flex flex--row flex--align-center">
      <a 
        class="flex flex--row flex--align-center mr-3"
        @click.stop=""
        :href="material.product_master.image"
        target="_blank"
        v-if="material.product_master.image"
        tabindex="-1"
      >
        <img 
          width="50px"
          height="auto"
          :src="material.product_master.image"
        />
      </a>
      <div class="columns is-multiline item__row">
        <div class="column is-2">
          <span class="help mb-1">Architectural Name</span>
          <div class="item__detail">
            {{ architectureName }}
          </div>
        </div>
        <div class="column is-2">
          <span class="help mb-1">
            {{ 
              material.product_master.product_category_id === 13 
                ? 'Panel' 
                : 'Railing' 
            }}
          </span>
          <div class="item__detail">
            {{ material.product_master.name }}
          </div>
        </div>
        <div class="column" v-if="isMitrex">
          <label class="help capitalize mb-1">
            Average kW/{{ isRailing ? 'LF' : 'SQFT' }}
          </label>
          <v-text-field
            type="number"
            required
            :value="$options.filters.round(material.manual_avg_kw_sqft, 2)"
            @input="material.manual_avg_kw_sqft = $event"
            @blur="updateMaterial('manual_avg_kw_sqft', $event)"
            @keyup.enter.native="updateMaterial('manual_avg_kw_sqft', $event)"
            :placeholder="`Average kW/${ isRailing ? 'LF' : 'SQFT' }`"
            :disabled="!editable"
          />
        </div>
        <div class="column" v-if="isMitrex">
          <label class="help capitalize mb-1">
            Active ($/{{ isRailing ? 'LF' : 'SQFT' }})
          </label>
          <v-text-field
            type="number"
            required
            :value="$options.filters.round(material.manual_active_dollar_sqft, 2)"
            @input="material.manual_active_dollar_sqft = $event"
            @blur="updateMaterial('manual_active_dollar_sqft', $event)"
            @enter="updateMaterial('manual_active_dollar_sqft', $event)"
            :placeholder="`Active Price per ${isRailing ? 'LF' : 'SQFT'}`"
            :disabled="!editable"
          />
        </div>
        <div class="column" v-if="!isRailing">
          <label class="help capitalize mb-1">
            Non Active ($/SQFT)
          </label>
          <v-text-field
            type="number"
            required
            :value="$options.filters.round(material.manual_non_active_dollar_sqft, 2)"
            @input="material.manual_non_active_dollar_sqft = $event"
            @blur="updateMaterial('manual_non_active_dollar_sqft', $event)"
            @enter="updateMaterial('manual_non_active_dollar_sqft', $event)"
            placeholder="Non Active Price per SQFT"
            :disabled="!editable"
          />
        </div>
        <div class="column" v-if="!isRailing">
          <label class="help capitalize mb-1">
            Returns ($/SQFT)
          </label>
          <v-text-field
            type="number"
            required
            :value="$options.filters.round(material.manual_returns_dollar_sqft, 2)"
            @input="material.manual_returns_dollar_sqft = $event"
            @blur="updateMaterial('manual_returns_dollar_sqft', $event)"
            @enter="updateMaterial('manual_returns_dollar_sqft', $event)"
            placeholder="Returns Price per SQFT"
            :disabled="!editable"
          />
        </div>
      </div>
    </div>

    <table class="table is-fullwidth is-hoverable has-small-font mb-0">
      <thead>
        <tr>
          <th></th>
          <th  
            v-for="(col, colIndex) in tableCols"
            class="capitalize"
            :key="colIndex"
          >
            {{ col.label }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr 
          v-for="(elevation, elevationIndex) in itemElevations" 
          :key="elevationIndex"
          class="v-centered"
        >
          <td>
            <button 
              class="delete" 
              @click="deleteElevation(elevation, elevationIndex)"
              v-if="!deletingElevations.includes(elevation.id) 
                && deletingElevations.length === 0"
              :disabled="!editable 
                || addingElevations.includes(elevationIndex)"
            />
            <div class="loader is-loading" v-else></div>
          </td>
          <td>
            <v-select
              v-if="elevations.length > 0"
              v-model="elevation.elevation_id"
              @input="updateElevation(elevation, 'elevation_id', $event, elevationIndex)"
              :options="elevations"
              item-text="elevation_name"
              item-value="id"
              :disabled="!editable 
                || deletingElevations.includes(elevation.id) 
                || addingElevations.includes(elevationIndex)
                || deletingElevations.length > 0"
            />
          </td>
          <td v-if="isMitrex">
            <v-text-field
              type="number"
              v-model="elevation.manual_active_sqft"
              placeholder="Total Active SQFT"
              @blur="updateElevation(elevation, 'manual_active_sqft', $event, elevationIndex)"
              @keyup.enter="updateElevation(elevation, 'manual_active_sqft', $event, elevationIndex)"
              :disabled="!editable 
                || deletingElevations.includes(elevation.id) 
                || addingElevations.includes(elevationIndex)
                || deletingElevations.length > 0"
            />
          </td>
          <td v-if="isMitrex">
            {{ elevation.manual_active_sqft * activeUnitPrice | currency }}
          </td>
          <td v-if="!isRailing">
            <v-text-field
              type="number"
              v-model="elevation.manual_non_active_sqft"
              placeholder="Total Non Active SQFT"
              @blur="updateElevation(elevation, 'manual_non_active_sqft', $event, elevationIndex)"
              @keyup.enter="updateElevation(elevation, 'manual_non_active_sqft', $event, elevationIndex)"
              :disabled="!editable 
                || deletingElevations.includes(elevation.id) 
                || addingElevations.includes(elevationIndex)
                || deletingElevations.length > 0"
            />
          </td>
          <td v-if="!isRailing">
            {{ elevation.manual_non_active_sqft * nonActiveUnitPrice | currency }}
          </td>
          <td v-if="!isRailing">
            <v-text-field
              type="number"
              v-model="elevation.manual_returns_and_flashings_sqft"
              placeholder="Total Returns and Flashings"
              @blur="updateElevation(elevation, 'manual_returns_and_flashings_sqft', $event, elevationIndex)"
              @keyup.enter="updateElevation(elevation, 'manual_returns_and_flashings_sqft', $event, elevationIndex)"
              :disabled="!editable 
                || deletingElevations.includes(elevation.id) 
                || addingElevations.includes(elevationIndex)
                || deletingElevations.length > 0"
            />
          </td>
          <td v-if="!isRailing">
            {{ elevation.manual_returns_and_flashings_sqft * returnsUnitPrice | currency }}
          </td>
          <td v-if="isMitrex">
            {{ elevation.manual_active_sqft * kwPerSqft / 1000 | round }}
          </td>
          <td v-if="isMitrex">
            {{ elevation.elevation_id 
                ? getKwhPerYear(elevation)
                : null | commasep }}
          </td>
        </tr>
      </tbody>
      <tfoot>
        <tr>
          <td :colspan="tableCols.length - 1"></td>
          <td class="has-background-light has-text-right">Item Total</td>
          <td class="has-text-weight-semibold has-background-light">
            {{ grandTotal | currency }}
          </td>
        </tr>
      </tfoot>
    </table>
    <button 
      class="button is-primary is-small ml-3 mt-2" 
      @click="addElevation"
      :disabled="!editable"
    >
      Add Elevation Details
    </button>
  </div>
</template>

<script>
import VTextField from '@/components/base/VTextField'
import VSelect from '@/components/base/VSelect'
import { condensedEstimationHelpers } from '@/mixins/helpers'
import { mapActions } from 'vuex'
const CELLS = 72
const PANEL_SQFT = 21.645

export default {
  props: {
    estimation: {
      type: Object
    },
    item: {
      type: Object
    },
    material: {
      type: Object,
      required: true
    },
    architectureName: {
      type: String
    },
    editable: {
      type: Boolean
    },
    sunHourDict: {
      type: Object
    },
    elevations: {
      type: Array
    }
  },
  components: { VTextField, VSelect },
  mixins: [condensedEstimationHelpers],
  data() {
    return {
      panelCols: [
        { label: 'Elevation' },
        { label: 'Active (SQFT)' },
        { label: '$' },
        { label: 'Non Active (SQFT)' },
        { label: '$' },
        { label: 'Returns and Flashings (LF)' },
        { label: '$'},
        { label: '~KW' },
        { label: 'BIPV~KWH/Year'}
      ],
      railingCols: [
        { label: 'Elevation' },
        { label: 'Active (LF)' },
        { label: '$' },
        { label: '~KW' },
        { label: 'BIPV~KWH/Year'}
      ],
      cladifyCols: [
        { label: 'Elevation' },
        { label: 'Area (SQFT)' },
        { label: '$' },
        { label: 'Returns and Flashings (LF)' },
        { label: '$' }
      ],
      itemElevations: [],
      deletingElevations: [],
      addingElevations: []
    }
  },
  computed: {
    isMitrex({ material }) {
      return material.product_master.product_brand === 'Mitrex'
    },
    isRailing({ material }) {
      return material.product_master.product_category_id === 20
    },
    tableCols({ isMitrex, isRailing, panelCols, railingCols, cladifyCols }) {
      return isMitrex 
        ? isRailing ? railingCols : panelCols 
        : cladifyCols
    },
    kwPerSqft({ material })  {
      return material.manual_avg_kw_sqft
    },
    activeUnitPrice({ material }) {
      return material.manual_active_dollar_sqft
    },
    nonActiveUnitPrice({ material }) {
      return material.manual_non_active_dollar_sqft
    },
    returnsUnitPrice({ material }) {
      return material.manual_returns_dollar_sqft
    }
  },
  mounted() {
    this.itemElevations = [...this.item.elevations.sort((a, b) => a.order - b.order)];
    if (this.itemElevations.length === 0) {
      this.addElevation();
    }

    const updates = {};
    if (!this.material['manual_avg_kw_sqft']) {
      const power = this.material.product_master.cell_power * CELLS
      updates['manual_avg_kw_sqft'] = power / PANEL_SQFT
    }
    if (!this.material['manual_active_dollar_sqft']) {
      updates['manual_active_dollar_sqft'] = this.material.product_master.unit_price_dpa
    }
    if (!this.material['manual_non_active_dollar_sqft']) {
      updates['manual_non_active_dollar_sqft'] = this.material.product_master.unit_price
    }
    if (!this.material['manual_returns_dollar_sqft']) {
      updates['manual_returns_dollar_sqft'] = this.material.product_master.unit_price_dpa
    }
    if (Object.entries(updates).length > 0) {
      this.updateItemMaterial({
        estimationId: this.estimation.id,
        itemMaterialId: this.material.id,
        data: updates,
        update: false
      })
    }
  },
  methods: {
    ...mapActions("cladifyEstimation", [
      "addItemElevation", "updateItemElevation", "deleteItemElevation",
      "updateItemMaterial"
    ]),
    ...mapActions("cladifyEstimation", ["setEstimation"]),
    getKwhPerYear(elevation) {
      const elevationName = this.elevations
        .find(e => e.id === elevation.elevation_id)?.elevation_name
      const elevationField = elevationName 
        ? this.estimation.manual_sun_hours_obj[elevationName.toLowerCase()] * 
          (elevation.manual_active_sqft * this.kwPerSqft / 1000)
        : 0
      return elevationField
    },
    updateMaterial(field, v) {
      const data = Object.fromEntries([[field, v]]);
      this.updateItemMaterial({
        estimationId: this.estimation.id,
        itemMaterialId: this.material.id,
        data: data,
        update: false
      })
    },
    addElevation() {
      const wallRailing = this.material.product_master.product_category_id === 20
        ? 'railing'
        : 'wall'
      this.$set(
        this.itemElevations,
        this.itemElevations.length,
        {
          id: null,
          order: Math.max(...this.itemElevations.map(ie => ie.order)),
          estimation_item_id: this.item.id,
          elevation_id: null,
          wall_railing: wallRailing,
          manual_active_sqft: 0,
          manual_non_active_sqft: 0,
          manual_returns_and_flashings_sqft: 0
        }
      )
    },
    updateElevation(elevation, field, v, index) {
      const data = Object.fromEntries([[field, v]])

      if (!elevation.id) {
        delete elevation.id;
        this.addingElevations.push(index);
        const addingIndex = this.addingElevations.indexOf(index);
        this.addItemElevation({
          estimation_id: this.estimation.id,
          ...elevation,
          ...data,
          order: index
        })
          .then(resp => {
            this.itemElevations.splice(index, 1, resp.data)
            this.addingElevations.splice(addingIndex, 1)
          })
          .catch(() => {
            this.addingElevations.splice(addingIndex, 1)
          })
      } else {
        this.updateItemElevation({
          itemElevationId: elevation.id,
          data: {
            area: 0,
            ...data
          },
          field
        })
      }
    },
    deleteElevation(elevation, elevationIndex) {
      if (elevation.id) {
        this.deletingElevations.push(elevation.id);
        const deletingIndex = this.deletingElevations.indexOf(elevation.id);
        this.deleteItemElevation(elevation.id)
          .then(() => {
            this.deletingElevations.splice(deletingIndex, 1)
            this.itemElevations.splice(elevationIndex, 1)
          })
          .catch(() => {
            this.deletingElevations.splice(deletingIndex, 1)
          })
      } else {
        this.itemElevations.splice(elevationIndex, 1)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/table.scss';
.capitalize {
  text-transform: capitalize;
}

.item__row {
  width: 100%;
}

.item__detail {
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 40px;
}

table tr th:first-child {
  width: 50px !important;
}
</style>